import { Link, useNavigate } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import type { CorpusCatalogueSubject } from "@shared/types/corpus/api.ts";
import { Section } from "../../../../components/primitives/layout/Section";
import {
  DataSequence,
  type DataSequenceColumn,
} from "../../../../components/primitives/data-display/DataSequence";
import { Meta } from "../../../../components/primitives/data-display/Meta";
import { LoadingState } from "../../../../components/primitives/feedback/LoadingState";
import { SearchBar } from "../../../../components/primitives/inputs/SearchBar";
import { useTaxonomy } from "../../../../context/TaxonomyProvider";
import {
  buildCatalogueDocumentsPath,
  nameCatalogueSubject,
} from "../../../../lib/corpusCatalogueView";
import { LibraryPage } from "../LibraryPage";
import { useCatalogueSubjects } from "./useCatalogueSubjects";

/** The corpus' main subjects, the reference a gap is worked against. A count is of ingested papers,
 *  and leads to the papers it is made of. */
export default function CatalogueSubjects() {
  const navigate = useNavigate();
  const taxonomy = useTaxonomy();
  const { field, setField, query, setQuery, subjects, loading, error, refresh, pagination } =
    useCatalogueSubjects();

  const fieldName = field ? (taxonomy.findField(field)?.enName ?? field) : null;

  const subjectColumns: DataSequenceColumn<CorpusCatalogueSubject>[] = [
    {
      key: "name",
      header: "Main subject",
      cardRole: "primary",
      cell: (row) => nameCatalogueSubject(row),
    },
    { key: "id", header: "Id", cell: (row) => <Meta>{row.subject}</Meta> },
    { key: "papers", header: "Papers", align: "right", cell: (row) => row.papers.toLocaleString() },
    {
      key: "action",
      header: "",
      cardRole: "action",
      cell: (row) => (
        <Button
          component={Link}
          to={buildCatalogueDocumentsPath({ field, subject: row.subject })}
          size="small"
          onClick={(event) => event.stopPropagation()}
        >
          Papers
        </Button>
      ),
    },
  ];

  return (
    <LibraryPage>
      <Section
        action={
          field ? (
            <Chip size="small" label={`Field: ${fieldName}`} onDelete={() => setField(null)} />
          ) : undefined
        }
      >
        <SearchBar
          value={query}
          onChange={setQuery}
          placeholder="Search a subject by name or id…"
          ariaLabel="Search subjects"
        />
        {loading ? (
          <LoadingState />
        ) : error && subjects.length === 0 ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={refresh}>
                Try again
              </Button>
            }
          >
            {error}
          </Alert>
        ) : (
          <DataSequence
            columns={subjectColumns}
            rows={subjects}
            getRowId={(row) => row.subject}
            pagination={pagination}
            onRowClick={(row) =>
              navigate(buildCatalogueDocumentsPath({ field, subject: row.subject }))
            }
            emptyMessage={
              query.trim() || field
                ? "No main subject matches."
                : "No ingested paper has a main subject yet."
            }
          />
        )}
      </Section>
    </LibraryPage>
  );
}
